import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {DxButtonComponent, DxDateRangeBoxComponent, DxSelectBoxComponent} from 'devextreme-angular';
import {DoughnutChartComponent} from '../../../../components/library/doughnut-chart/doughnut-chart.component';
import {TrendingComponent} from '../../../../components/library/trending/trending.component';
import {CapitalCasePipe} from '../../../../pipes/capital-case.pipe';
import {
    AggregateType,
    FacilityUtilizationScoreExportQuery,
    FacilityUtilizationScoreQuery,
    FacilityUtilizationScoreType,
    FacilityUtilizationTrendingQuery,
    InventoryClient,
    ITrendingQuery
} from '@services';
import EnumUtils from '../../../../shared/enum-utils';
import {FileUtils} from '../../../../shared/file-utils';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

export function facilityUtilizationPalette(): Record<string, string> {
    const getColor = (name: string) => getComputedStyle(document.body).getPropertyValue(name).trim();
    return {
        ["Full"]: getColor('--bs-danger'),
        ["Partial"]: getColor('--bs-warning'),
        ["Empty"]: getColor('--bs-success'),
    };
}

@Component({
    selector: 'app-facility-utilization-score',
    standalone: true,
    imports: [
        CommonModule,
        DxDateRangeBoxComponent,
        DxButtonComponent,
        DxSelectBoxComponent,
        DoughnutChartComponent,
        TrendingComponent,
        CapitalCasePipe
    ],
    templateUrl: './facility-utilization-score.component.html'
})
export class FacilityUtilizationScoreComponent implements OnInit {
    private inventoryClient = inject(InventoryClient);
    private destroyRef = inject(DestroyRef);

    title = 'My Score';
    trendingTitle = 'Trending';
    dateRangeFilterValue: [Date, Date] = [
        new Date(new Date().setHours(0, 0, 0, 0)),
        new Date(new Date().setHours(0, 0, 0, 0)),
    ];

    trendingFilterOptions = EnumUtils.getEnumOptions(AggregateType);
    trendingFilterValue: AggregateType = AggregateType.Day;

    palette: Record<string, string> = {};

    readonly chartTypes = [
        FacilityUtilizationScoreType.FacilityUsage,
        FacilityUtilizationScoreType.CoolerUsage,
        FacilityUtilizationScoreType.AmbientUsage
    ];

    ngOnInit(): void {
        this.palette = facilityUtilizationPalette();
    }

    get fromDate(): Date {
        return this.dateRangeFilterValue?.[0];
    }

    get toDate(): Date {
        return this.dateRangeFilterValue?.[1];
    }

    onDateRangeChanged(e: any): void {
        if (e.value) {
            this.dateRangeFilterValue = e.value;
        }
    }

    loadScoreData = (scoreType: FacilityUtilizationScoreType) => {
        return (query: any) => {
            const q = new FacilityUtilizationScoreQuery();
            q.init({...query, scoreType: scoreType});
            return this.inventoryClient.getFacilityUtilizationScore(q);
        };
    }

    loadTrendingData = (query: ITrendingQuery) => {
        const q = new FacilityUtilizationTrendingQuery();
        q.init({...query});
        return this.inventoryClient.getFacilityUtilizationTrending(q);
    }

    onExport(): void {
        const query = new FacilityUtilizationScoreExportQuery({
            from: this.fromDate,
            to: this.toDate
        });
        this.inventoryClient.exportFacilityUtilizationScore(query)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((res) => FileUtils.downloadFile(res));
    }
}
